import { useTranslation } from '@/src/i18n';
import { Ionicons } from '@expo/vector-icons';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { createStyles } from './EditProfileScreen.styles';

export interface EditProfileScreenProps {
  fullName: string;
  setFullName: (value: string) => void;
  bio: string;
  setBio: (value: string) => void;
  avatarUrl: string;
  email: string;
  loading: boolean;
  uploading: boolean;
  getInitials: () => string;
  handleSave: () => void;
  handleCancel: () => void;
  pickImage: () => void;
  colors: {
    background: string;
    surface: string;
    border: string;
    primary: string;
    primaryText: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
  };
}

export const EditProfileScreen = ({
  fullName,
  setFullName,
  bio,
  setBio,
  avatarUrl,
  email,
  loading,
  uploading,
  getInitials,
  handleSave,
  handleCancel,
  pickImage,
  colors,
}: EditProfileScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView
        style={[styles.container, { backgroundColor: colors.background }]}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Avatar Section */}
        <View style={styles.avatarSection}>
          {avatarUrl ? (
            <Image source={{ uri: avatarUrl }} style={styles.avatarImage} />
          ) : (
            <View style={[styles.avatar, { backgroundColor: colors.primary }]}>
              <Text style={styles.avatarText}>{getInitials()}</Text>
            </View>
          )}

          <TouchableOpacity
            style={[styles.changePhotoBtn, { backgroundColor: colors.surface, borderColor: colors.border }]}
            onPress={pickImage}
            disabled={uploading}
          >
            {uploading ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : (
              <View style={styles.changePhotoBtnContent}>
                <Ionicons name="camera" size={18} color={colors.primary} />
                <Text style={[styles.changePhotoText, { color: colors.primary }]}>
                  {avatarUrl ? t('editProfile.changePhoto') : t('editProfile.uploadPhoto')}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>

        {/* Form Section */}
        <View style={[styles.section, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <Text style={[styles.label, { color: colors.textSecondary }]}>{t('editProfile.fullName')}</Text>
          <TextInput
            style={[styles.input, { color: colors.text, borderColor: colors.border, backgroundColor: colors.background }]}
            value={fullName}
            onChangeText={setFullName}
            placeholder={t('editProfile.enterFullName')}
            placeholderTextColor={colors.textTertiary}
            autoCapitalize="words"
            editable={!loading}
          />
        </View>

        <View style={[styles.section, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <Text style={[styles.label, { color: colors.textSecondary }]}>{t('editProfile.email')}</Text>
          <TextInput
            style={[styles.input, { color: colors.textSecondary, borderColor: colors.border, backgroundColor: colors.background }]}
            value={email}
            editable={false}
          />
          <Text style={[styles.helpText, { color: colors.textTertiary }]}>
            {t('editProfile.emailCannotChange')}
          </Text>
        </View>

        <View style={[styles.section, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <Text style={[styles.label, { color: colors.textSecondary }]}>{t('editProfile.bio')}</Text>
          <TextInput
            style={[styles.textArea, { color: colors.text, borderColor: colors.border, backgroundColor: colors.background }]}
            value={bio}
            onChangeText={setBio}
            placeholder={t('editProfile.bioPlaceholder')}
            placeholderTextColor={colors.textTertiary}
            multiline
            numberOfLines={4}
            maxLength={200}
            editable={!loading}
          />
          <Text style={[styles.charCount, { color: colors.textTertiary }]}>
            {bio.length}/200
          </Text>
        </View>

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveBtn, { backgroundColor: colors.primary, opacity: loading ? 0.6 : 1 }]}
          onPress={handleSave}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator color={colors.primaryText} />
          ) : (
            <View style={styles.saveBtnContent}>
              <Ionicons name="checkmark-circle" size={20} color={colors.primaryText} />
              <Text style={[styles.saveBtnText, { color: colors.primaryText }]}>{t('editProfile.saveChanges')}</Text>
            </View>
          )}
        </TouchableOpacity>

        {/* Cancel Button */}
        <TouchableOpacity
          style={[styles.cancelBtn, { borderColor: colors.border }]}
          onPress={handleCancel}
          disabled={loading}
        >
          <View style={styles.cancelBtnContent}>
            <Ionicons name="close-circle-outline" size={20} color={colors.text} />
            <Text style={[styles.cancelBtnText, { color: colors.text }]}>{t('common.cancel')}</Text>
          </View>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};
